import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Company } from "src/app/models/Company";
import { Reminder } from "src/app/models/Reminder";
import { RemindersByCompany } from "src/app/models/RemindersByCompany";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalService } from "src/app/services/modal.service";

import { ConfirmationDialogData } from "../common/confirmation-dialog/confirmation-dialog-modal.component";
import { ReminderService } from "./../../services/reminder.service";
import { ReminderModalComponent } from "./../reminder-modal/reminder-modal.component";

@Component({
  selector: "app-reminders",
  templateUrl: "./reminders.component.html",
  styleUrls: ["./reminders.component.scss"],
  standalone: false,
})
export class RemindersComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private subscriptions: Subscription[] = [];
  faSpinner = faSpinner;

  subscription: Subscription;
  dataSource = new MatTableDataSource<Reminder>([]);
  isLoading: boolean;
  columns = [
    { key: "type", title: LocalizationConstants.REMINDER_TYPE },
    { key: "interval", title: LocalizationConstants.REMINDER_INTERVAL },
    { key: "daysBefore", title: LocalizationConstants.REMINDER_DAYS_BEFORE },
    { key: "daysAfter", title: LocalizationConstants.REMINDER_DAYS_AFTER },
    { key: "to", title: LocalizationConstants.REMINDER_TO },
    { key: "cc", title: LocalizationConstants.REMINDER_CC },
    { key: "bcc", title: LocalizationConstants.REMINDER_BCC },
  ];

  columnKeysAll = [...this.columns.map((x) => x.key), "action1", "action2"];

  constructor(
    private reminderService: ReminderService,
    public companyService: CompanyService,
    private modalService: ModalService,
    private globalOverlayService: GlobalOverlayService,
  ) {}

  ngOnInit() {
    this.subscription = this.onCompanyChange();
    this.getReminders(this.companyService.selectedCompany);
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getReminders(company: Company) {
    if (!company) return;

    this.globalOverlayService.show();
    this.reminderService
      .get(company.name)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => (this.dataSource.data = response));
  }

  create() {
    const reminder = new Reminder(this.companyService.selectedCompany.name);
    this.openModal(reminder);
  }

  createDefaults() {
    this.globalOverlayService.show();
    this.reminderService
      .getDefaultEmails(this.companyService.selectedCompany.name)
      .subscribe((emails) => {
        if (!emails) return null;

        this.reminderService
          .createDefaults(
            new RemindersByCompany(this.companyService.selectedCompany.name, this.dataSource.data),
            emails,
          )
          .pipe(finalize(() => this.globalOverlayService.close()))
          .subscribe((defaultReminders) => {
            if (!defaultReminders) return;
            this.dataSource.data = [...this.dataSource.data, ...defaultReminders];
          });
      });
  }

  private remove(reminder: Reminder) {
    reminder.pending = true;
    this.reminderService.delete(reminder.id).subscribe(
      () => {
        reminder.pending = false;
        this.dataSource.data = this.dataSource.data.filter((r) => r.id != reminder.id);
      },
      () => (reminder.pending = false),
    );
  }

  async onRemoveReminder(reminder: Reminder): Promise<void> {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.REMINDERS_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.REMINDERS_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const canProceed = await resultObservable.toPromise();
    if (canProceed) {
      this.remove(reminder);
    }
  }

  onCompanyChange() {
    return this.companyService.companyChange.subscribe((company) => {
      if (!company?.name) return;
      this.getReminders(company);
    });
  }

  onEditReminder(reminder: Reminder) {
    this.openModal(reminder);
  }

  openModal(reminder: Reminder) {
    const resultObservable = this.modalService.openDefaultModal<
      ReminderModalComponent,
      Reminder,
      Reminder
    >(ReminderModalComponent, reminder);

    const subscription = resultObservable.subscribe((result) => {
      // modal just closed without save
      if (!result) {
        return;
      }

      const existingReminderIndex = this.dataSource.data.findIndex((x) => x.id == result.id);

      if (existingReminderIndex == -1) {
        this.dataSource.data.push(result);
      } else {
        this.dataSource.data[existingReminderIndex] = result;
      }

      this.dataSource.data = [...this.dataSource.data];
    });
    this.subscriptions.push(subscription);
  }

  get allRemindersCreated(): boolean {
    return (
      this.dataSource.data &&
      this.reminderService.types.filter((x) => !this.dataSource.data.some((r) => r.type === x.key))
        .length === 0
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
